<script setup lang="ts">
  import * as changeCase from 'change-case';
  import pluralize from 'pluralize';
  import { computed, inject, onBeforeMount, ref } from 'vue';

  import type { Attachment, Model } from '@stuut/api/models';
  import { formatFileSize } from '@stuut/common/src/utils';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';

  import { FileDropzoneKey } from '../file-upload-dropzone/types';
  import type { FileDropzoneProviderContext } from '../file-upload-dropzone/types';

  type EntityModel = Model & { id?: string };

  interface Props {
    attachment: Attachment;
    entityId?: string;
    entityModel?: EntityModel;
    entityName?: string;
    index?: number;
    presignUrlFnName?: string;
    presignUrlParams?: Record<string, any>;
  }

  const props = withDefaults(defineProps<Props>(), {
    presignUrlFnName: 'getAttachmentPresignedUrl',
  });

  const emit = defineEmits<{
    (e: 'uploaded', entity: any): void;
    (e: 'failed', attachment: Attachment): void;
  }>();

  const progressValue = ref(0);
  const filesize = ref(0);
  const failed = ref(false);

  const onUploadProgress = (progressEvent) => {
    progressValue.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
  };

  const modelName = computed(() => props.entityName || props.entityModel?.object);
  const modelId = computed(() => props.entityId || props.entityModel?.id);
  const resourceName = computed(() => pluralize(changeCase.camelCase(modelName.value)));

  const fileDropzone = inject<FileDropzoneProviderContext | undefined>(FileDropzoneKey, undefined);

  const uploadAttachment = async () => {
    if (
      !props.attachment?._file ||
      props.attachment?.is_uploading ||
      props.attachment.is_uploaded
    ) {
      return;
    }

    props.attachment.is_uploading = true;
    const file = props.attachment?._file;

    filesize.value = file.size;

    try {
      if (!props.presignUrlFnName) return;

      const resource = stuut[resourceName.value];
      if (!resource || typeof resource[props.presignUrlFnName] !== 'function') {
        throw new Error(
          `Presign URL function ${props.presignUrlFnName} not found on ${resourceName.value}`,
        );
      }

      const data = await resource[props.presignUrlFnName](
        modelId.value,
        file.name,
        file.type,
        props.presignUrlParams,
      );

      props.attachment.id = data.attachment.id;

      // Extract entity from response (any key other than presignedUrl/attachment)
      const entityKeys = Object.keys(data).filter(
        (key) => key !== 'presignedUrl' && key !== 'attachment',
      );
      const entity = entityKeys.length > 0 ? data[entityKeys[0]] : undefined;

      await stuut.uploadAttachment(props.attachment, data.presignedUrl, file, onUploadProgress);

      if (props.entityModel?.id) {
        await stuut[resourceName.value]?.verifyAttachment(props.entityModel, props.attachment);
      }

      props.attachment._file = null;
      props.attachment.is_uploaded = true;

      // Emit the uploaded entity
      if (entity) {
        emit('uploaded', entity);
      }
    } catch (err: any) {
      failed.value = true;

      // Show the actual error message from the server
      const errorMessage =
        err.clientMessage || err.message || 'An error occurred while uploading the attachment.';
      toast.error(errorMessage);

      // Try to remove the attachment from the backend if it was created
      if (props.attachment.id && props.entityModel?.id) {
        try {
          await removeAttachment(props.entityModel, props.attachment);
        } catch (removeErr) {
          console.error('Failed to remove attachment:', removeErr);
        }
      }

      // Emit failed event so parent can remove from UI
      emit('failed', props.attachment);
    } finally {
      props.attachment.is_uploading = false;
    }
  };

  const removeAttachment = (entity: EntityModel, attachment: Attachment) => {
    try {
      const resource = stuut[resourceName.value];

      if (!resource || typeof resource.removeAttachment !== 'function') {
        throw new Error(`removeAttachment fn not found on ${resourceName.value}`);
      }
      fileDropzone?.removeFileAttachment(attachment);
      return resource?.removeAttachment(entity, attachment);
    } catch (err: any) {
      toast.error(err);
    }
  };

  const formattedFileSize = computed(() => {
    return formatFileSize(filesize.value);
  });

  onBeforeMount(() => {
    uploadAttachment();
  });
</script>

<template>
  <slot
    v-bind="{
      attachment,
      file: attachment?._file,
      progressValue,
      failed,
      filesize: formattedFileSize,
      removeAttachment,
    }"
  />
</template>

<style scoped></style>
